import { School, POI, POIs, Schools } from "@local-logic/client";
import { BBox, Feature, Point } from "@turf/turf";
import React from "react";
import { MapRef, Marker as M } from "react-map-gl/maplibre";
import Supercluster from "supercluster";
import useSupercluster from "use-supercluster";
import { UseMeasureRect } from "~/hooks/useMeasure";
import Marker from "./Marker";
import Cluster from "./Cluster";

type PointCluster = Supercluster.PointFeature<{
  poi: School | POI;
  type: string;
}>;
type Cluster = Supercluster.PointFeature<Supercluster.ClusterProperties>;

interface MarkersProps {
  mapRef: React.MutableRefObject<MapRef | undefined>;
  points: Feature<
    Point,
    {
      poi: POI | School;
      type: string;
    }
  >[];
  zoom: number;
  activePOI: POI | School | undefined;
  hoveredPOI: POI | School | undefined;
  isMapMoving: boolean;
  isMobile: boolean;
  rect: UseMeasureRect;
}

function isCluster(cluster: PointCluster | Cluster): cluster is Cluster {
  return (cluster as Cluster).properties.cluster !== undefined;
}

const Markers = React.memo(
  ({
    rect,
    zoom,
    mapRef,
    points,
    isMobile,
    activePOI,
    hoveredPOI,
    isMapMoving,
  }: MarkersProps) => {
    const bounds = mapRef?.current
      ?.getMap()
      .getBounds()
      .toArray()
      .flat() as BBox;

    const { clusters, supercluster } = useSupercluster({
      points,
      bounds,
      zoom,
      options: { radius: 50, maxZoom: 30 },
    });

    return (
      <>
        {clusters.map((cluster, i) => {
          const [longitude, latitude] = cluster.geometry.coordinates;

          if (isCluster(cluster)) {
            return (
              <M
                key={`cluster-${cluster.id}`}
                latitude={latitude}
                longitude={longitude}
              >
                <Cluster
                  rect={rect}
                  zoom={zoom}
                  cluster={cluster}
                  supercluster={supercluster}
                  pointsCount={points.length}
                  activePOI={activePOI}
                  isMobile={isMobile}
                  isMapMoving={isMapMoving}
                  hoveredPOI={hoveredPOI}
                  onClick={() => {
                    const expansionZoom = Math.min(
                      (
                        supercluster as Supercluster.AnyProps
                      ).getClusterExpansionZoom(cluster.id),
                      20,
                    );

                    mapRef.current?.flyTo({
                      zoom: expansionZoom,
                      center: [longitude, latitude],
                      duration: 1000,

                      // Without this, some operating systems will prevent this from
                      // working. See comment:
                      // https://docs.mapbox.com/mapbox-gl-js/example/flyto-options/
                      essential: true,
                    });
                  }}
                />
              </M>
            );
          }

          return (
            <Marker
              key={i}
              rect={rect}
              poi={cluster.properties.poi}
              poiType={cluster.properties.type as keyof POIs | keyof Schools}
              isMobile={isMobile}
              activePOI={activePOI}
              hoveredPOI={hoveredPOI}
              isMapMoving={isMapMoving}
            />
          );
        })}
      </>
    );
  },
);

Markers.displayName = "Markers";

export default Markers;
